import * as React from "react";
import { V2Shell } from "@/v2/components/V2Shell";
import useConfig from "@/hooks/useConfig";
import {
  formatGameTime,
  formatGameWeekdayDate,
  parseGameDateTime,
} from "@/v2/pages/Events/date-utils";
import { GameCountdown, type CountdownTeam, type GamePreviewContent } from "./GameCountdown";
import { GameCountdownSkeleton } from "./GameCountdownSkeleton";
import { optionalSeasonRecord, statsFor } from "./gameSheetRecord";
import { teamNameParts } from "./teamName";
import { useGameSheetBoxScore } from "./useGameSheetBoxScore";
import { useGameSummary } from "./useGameSummary";

/**
 * The v2 countdown screen, wired to the data the watch page already has plus
 * the two optional upstreams that fill it out.
 *
 * THE SHELL AND THE HOOKS HAVE TO BE TWO COMPONENTS. V2Shell renders the
 * QueryClientProvider, so a hook called in the same component that renders the
 * shell sits above the provider and never sees it. Same split as
 * CondensedClipsPanel in VideoPlayerLive.
 */

/** How early the stream is considered available, matching the v1 flip clock. */
const STREAM_LEAD_MINUTES = 15;

export type GameCountdownPanelProps = {
  /** The player API's game row, straight from the watch page. */
  gameDetails: Record<string, any> | null | undefined;
  /** The watch page has not resolved the game yet: render the skeleton. */
  loading?: boolean;
  onComplete?: () => void;
};

export function GameCountdownPanel(props: GameCountdownPanelProps) {
  return (
    <V2Shell>
      <GameCountdownContent {...props} />
    </V2Shell>
  );
}

/**
 * Splits "2026-2027 NAHL Regular Season" into the season's name and its years.
 *
 * GameSheet writes the two together in one title; the design shows them on two
 * lines. A title with no year range keeps its whole string as the name, so a
 * season named some other way is printed as given rather than mangled.
 */
export function splitSeasonTitle(title?: string | null): {
  name: string | null;
  years: string | null;
} {
  const raw = (title ?? "").trim();
  if (!raw) return { name: null, years: null };

  const match = raw.match(/(\d{4})\s*[-–/]\s*(\d{2,4})/);
  if (!match) return { name: raw, years: null };

  const [start, endRaw] = [match[1], match[2]];
  // "2026-27" and "2026-2027" both mean the same two seasons; print the long form.
  const end = endRaw.length === 4 ? endRaw : `${start.slice(0, 4 - endRaw.length)}${endRaw}`;
  /*
   * Trim the separator the years were attached by, or "FALL Tournament -
   * 2026-2027" becomes "FALL Tournament -" with a hyphen pointing at nothing.
   * Both ends, since upstream writes the range on either side of the name.
   */
  const name = raw
    .replace(match[0], "")
    .replace(/\s+/g, " ")
    .replace(/^[\s\-–/,:]+|[\s\-–/,:]+$/g, "")
    .trim();
  return { name: name || raw, years: `${start} - ${end}` };
}

/** Full display name, rebuilt from the two fields the player API splits it into. */
function fullTeamName(team?: string | null, mascot?: string | null): string {
  const parts = teamNameParts(team ?? undefined, mascot ?? undefined);
  return [parts.city, parts.mascot].filter(Boolean).join(" ").trim();
}

function GameCountdownContent({
  gameDetails,
  loading = false,
  onComplete,
}: GameCountdownPanelProps) {
  const config = useConfig();

  /*
   * GameSheet is where the records and the season name come from, and it is the
   * one upstream most games do not have. The hook fires only for a game that
   * carries a GameSheet id AND a tenant an upstream actually serves
   * (`config.v2.gameDetails`); short of either, the screen simply shows no
   * records and no season, per the design.
   *
   * The tenant half matters as much as the game half. Nine of the thirteen
   * tenants ship an empty `REACT_APP_GS_URL`, which leaves the axios baseURL
   * empty and sends `/nahlboxscore/:id` to the SPA's own origin, where the
   * router answers every path with the index document. That is a request per
   * pre-game screen that can only ever resolve to nothing.
   */
  const gameDetailsEnabled = config?.v2?.gameDetails === true;
  const { data: gsDetails, isFetching: gsFetching } = useGameSheetBoxScore(
    gameDetails?.game_id_gs,
    gameDetailsEnabled,
  );
  const boxScore = gsDetails?.boxScore ?? null;
  const matchup = gsDetails?.matchup ?? null;

  const { data: summary, isFetching: summaryFetching } = useGameSummary(
    gameDetails?.game_id,
  );

  /*
   * Only a game that CAN produce a record holds the space for one. Everywhere
   * else the record line is absent from the first frame rather than shimmering
   * into nothing. `gsFetching` is already false whenever the query above is
   * disabled, so the tenant half is covered without restating it.
   */
  const recordLoading = Boolean(gameDetails?.game_id_gs) && gsFetching;

  const awayTeam: CountdownTeam = {
    name: fullTeamName(gameDetails?.away_team, gameDetails?.away_team_mascot),
    logo: gameDetails?.away_team_logo || null,
    // `visitor` is GameSheet's name for the away side, not `away`.
    record: boxScore
      ? optionalSeasonRecord(statsFor(matchup, boxScore.visitor.id), boxScore.visitor.record)
      : null,
    recordLoading,
  };

  const homeTeam: CountdownTeam = {
    name: fullTeamName(gameDetails?.home_team, gameDetails?.home_team_mascot),
    logo: gameDetails?.home_team_logo || null,
    record: boxScore
      ? optionalSeasonRecord(statsFor(matchup, boxScore.home.id), boxScore.home.record)
      : null,
    recordLoading,
  };

  const gameDateTime: string | undefined = gameDetails?.game_date_time || undefined;

  /*
   * The clock runs to fifteen minutes before face-off, which is when the
   * stream comes up; the time printed above it is face-off itself. That is the
   * v1 behaviour, kept deliberately: shortening the clock to match the printed
   * time would have viewers arrive to a stream that is already running.
   */
  const streamAvailableAtMs = React.useMemo(() => {
    if (!gameDateTime) return null;
    const parsed = parseGameDateTime(gameDateTime);
    if (!parsed.isValid()) return null;
    return parsed.subtract(STREAM_LEAD_MINUTES, "minute").valueOf();
  }, [gameDateTime]);

  const season = splitSeasonTitle(boxScore?.season?.title);

  /*
   * The preview is the pre-game half of the game's write-up. The recap half
   * (PostGame*) is deliberately not read here: after a game this screen is not
   * on screen, and showing a recap above a countdown would be nonsense.
   */
  const preview: GamePreviewContent | null = React.useMemo(() => {
    const title = summary?.Title?.trim() || null;
    const short = summary?.PreGameShortSummary?.trim() || null;
    const long = summary?.PreGameSummary?.trim() || null;
    if (!title && !short && !long) return null;
    return { title, summary: short, full: long };
  }, [summary]);

  /*
   * The shell stays mounted across the switch, so the skeleton and the real
   * screen share a query client and the fetches started above keep running
   * while it shows. Placed after every hook, per the rules of hooks.
   */
  if (loading) return <GameCountdownSkeleton />;
  if (!gameDetails) return null;

  return (
    <GameCountdown
      /*
       * config.logoUrl, the square mark the sign-in card uses, not
       * config.headerLogo: the header's wide lockup is drawn to sit in a 64px
       * bar and reads as a second header when it opens a page.
       */
      brand={{
        logo: config?.logoUrl,
        name: config?.privacyText || config?.mainName || config?.title,
      }}
      awayTeam={awayTeam}
      homeTeam={homeTeam}
      /*
       * The team's own brand colour, which the player API carries for a team
       * that has one. It is an EMPTY STRING far more often than not, so the
       * tenant's own two colours are the fallback: the art stays on-brand
       * instead of dropping to a grey slab.
       */
      awayTeamColor={gameDetails?.away_team_primary_color || config?.primary}
      homeTeamColor={gameDetails?.home_team_primary_color || config?.secondary}
      streamAvailableAtMs={streamAvailableAtMs}
      gameDateLabel={formatGameWeekdayDate(gameDateTime) || null}
      gameTimeLabel={formatGameTime(gameDateTime) || null}
      caption={gameDetails?.caption || null}
      venueName={gameDetails?.facility_name || null}
      venueDetail={gameDetails?.pad_name || boxScore?.arena?.location || null}
      seasonName={season.name}
      seasonDetail={season.years}
      preview={preview}
      previewImageUrl={gameDetails?.card_image_url_wide || null}
      previewLoading={summaryFetching}
      onComplete={onComplete}
    />
  );
}

export default GameCountdownPanel;
